import { triggerActive } from '@icontactic/logic';

/**
 * F3E2-04: initial options straight from the purchase gate's server-rendered JSON, so the component's first
 * render does not depend on module execution order (the gate broadcasts `ict:options-change` only once).
 */
function initialOptionsFrom(root) {
  try {
    const payload = root.querySelector('ict-purchase-gate script[type="application/json"]');
    if (!payload) return null;
    const cfg = JSON.parse(payload.textContent || '{}');
    const names = cfg.optionNames || [];
    const values = cfg.selected?.options || [];
    const out = {};
    names.forEach((n, i) => { if (values[i] !== undefined) out[n] = values[i]; });
    return out;
  } catch { return null; }
}

/**
 * <ict-acknowledgment> — generic required acknowledgment (size confirmation, custom-item final sale,
 * compatibility acknowledgment). Label and copy are merchant settings; nothing legal is hardcoded.
 * Value is stored as a line-item property (e.g. "Size confirmed: Yes") via the purchase gate.
 */
class IctAcknowledgment extends HTMLElement {
  #active = true;
  #abort = new AbortController();

  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    this.errorEl = this.querySelector('[data-ict-error]');
    this.trigger = { option: this.dataset.triggerOption || '', value: this.dataset.triggerValue || '' };
    this.property = this.dataset.property || 'Acknowledged';
    this.value = this.dataset.value || 'Yes';
    this.required = this.dataset.required !== 'false';
    this.message = this.dataset.message || '';
    this.touched = false;

    const { signal } = this.#abort;
    this.input?.addEventListener('change', () => { this.touched = true; this.#update(); }, { signal });
    const root = this.closest('[data-ict-scope]') || this.closest('.shopify-section') || document.body;
    root.addEventListener('ict:options-change', this.#onOptions, { signal });
    root.addEventListener('ict:gate-ready', () => this.#update(), { signal });
    const initial = initialOptionsFrom(root);
    if (initial) this.#active = triggerActive(this.trigger, initial);
    this.#update();
  }

  disconnectedCallback() { this.#abort.abort(); }

  #onOptions = (event) => {
    const active = triggerActive(this.trigger, event.detail?.options);
    if (active !== this.#active) {
      this.#active = active;
      if (!active && this.input) { this.input.checked = false; this.touched = false; }
      this.#update();
    }
  };

  #update() {
    this.hidden = !this.#active;
    this.classList.toggle('ict-hidden-important', !this.#active);
    const checked = !!this.input?.checked;
    const valid = !this.required || checked;
    if (this.input) this.input.setAttribute('aria-invalid', String(this.#active && this.touched && !valid));
    if (this.errorEl) this.errorEl.textContent = this.#active && this.touched && !valid ? this.message : '';
    this.dispatchEvent(
      new CustomEvent('ict:component-state', {
        bubbles: true,
        detail: {
          id: this.id,
          kind: 'acknowledgment',
          active: this.#active,
          required: this.required,
          valid,
          code: 'acknowledgment_required',
          message: valid ? '' : this.message,
          properties: checked ? { [this.property]: this.value } : {},
          trigger: this.trigger,
        },
      })
    );
  }
}

if (!customElements.get('ict-acknowledgment')) customElements.define('ict-acknowledgment', IctAcknowledgment);